/**
 * A tiny scoped pub/sub between the workflow canvas — which owns the bpmn modeler and the
 * collab op stacks, and is therefore the only place that can answer "is there anything to
 * undo?" — and the sub-bar's history buttons, which render OUTSIDE its subtree. Same shape
 * and reasoning as `process-editor-tab-bus`: a module-local emitter keeps a canvas-only
 * concern out of the Top-risk shared screen-design model.
 *
 * Nothing is retained across an unmount. A retained "can undo" would outlive the diagram it
 * described and light up the buttons over an empty canvas, so the window resets the state on
 * the way out; a request with no canvas mounted is simply dropped, since a click only means
 * anything while one is on screen.
 */

export type WorkflowHistoryDirection = "undo" | "redo";

/** What the buttons need in order to render: whether either direction has anything in it. */
export interface IWorkflowHistoryState {
  canUndo: boolean;
  canRedo: boolean;
}

export const IDLE_WORKFLOW_HISTORY: IWorkflowHistoryState = { canUndo: false, canRedo: false };

type HistoryStateListener = (state: IWorkflowHistoryState) => void;
type HistoryRequestListener = (direction: WorkflowHistoryDirection) => void;

let historyState: IWorkflowHistoryState = IDLE_WORKFLOW_HISTORY;
const stateListeners: Set<HistoryStateListener> = new Set();
const requestListeners: Set<HistoryRequestListener> = new Set();

/** The canvas publishes after every command-stack change (and on reset). */
export const publishWorkflowHistoryState = (next: IWorkflowHistoryState): void => {
  if (next.canUndo === historyState.canUndo && next.canRedo === historyState.canRedo) return;
  historyState = next;
  stateListeners.forEach((listener: HistoryStateListener): void => listener(historyState));
};

/** The last published state — the buttons' initial value when they mount mid-session. */
export const getWorkflowHistoryState = (): IWorkflowHistoryState => historyState;

/** Observe the history state; returns an unsubscribe for cleanup. */
export const subscribeWorkflowHistoryState = (listener: HistoryStateListener): (() => void) => {
  stateListeners.add(listener);
  return (): void => {
    stateListeners.delete(listener);
  };
};

/** A button asks the open canvas to step its history. Dropped when no canvas is mounted. */
export const requestWorkflowHistory = (direction: WorkflowHistoryDirection): void => {
  requestListeners.forEach((listener: HistoryRequestListener): void => listener(direction));
};

/** The canvas subscribes; returns an unsubscribe for cleanup. */
export const subscribeWorkflowHistoryRequests = (listener: HistoryRequestListener): (() => void) => {
  requestListeners.add(listener);
  return (): void => {
    requestListeners.delete(listener);
  };
};
